/**
 * Global Command Bar (Ctrl/Cmd+K).
 *
 * Additive UI only: every result navigates to an EXISTING route and every
 * query respects existing permissions/RLS. The AI tab talks to a provider
 * interface so the rule engine can be swapped for an LLM with no UI change.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, Search as SearchIcon, ArrowRight } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useCompany } from "@/hooks/useCompany";
import { useUserRole } from "@/hooks/useUserRole";
import { searchCommandBar, type CommandGroup as CommandGroupData } from "@/services/commandSearch";
import { getCommandAiProvider, type CommandAiAnswer } from "@/services/commandAi";

interface CommandBarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Tab = "search" | "ai";

export function CommandBar({ open, onOpenChange }: CommandBarProps) {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const { isDriver } = useUserRole();

  const [tab, setTab] = useState<Tab>("search");
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<CommandGroupData[]>([]);
  const [searching, setSearching] = useState(false);

  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState<CommandAiAnswer | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const ctx = useMemo(() => ({ companyId, isDriver }), [companyId, isDriver]);
  const provider = useMemo(() => getCommandAiProvider(), []);
  const requestId = useRef(0);

  useEffect(() => {
    if (!open) return;
    setTab("search");
    setQuery("");
    setAiQuestion("");
    setAiAnswer(null);
    setAiError(null);
  }, [open]);

  useEffect(() => {
    if (!open || tab !== "search") return;
    const id = ++requestId.current;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await searchCommandBar(query, ctx);
        if (id === requestId.current) setGroups(result);
      } catch {
        if (id === requestId.current) setGroups([]);
      } finally {
        if (id === requestId.current) setSearching(false);
      }
    }, query.trim().length < 2 ? 0 : 200);
    return () => window.clearTimeout(timer);
  }, [query, open, tab, ctx]);

  const go = useCallback(
    (to: string) => {
      onOpenChange(false);
      navigate(to);
    },
    [navigate, onOpenChange],
  );

  const ask = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || !companyId) {
        if (!companyId) setAiError("No company context available yet.");
        return;
      }
      setAiQuestion(trimmed);
      setAiLoading(true);
      setAiError(null);
      try {
        const answer = await provider.answer(trimmed, { companyId, isDriver });
        setAiAnswer(answer);
      } catch {
        setAiError("Couldn't answer that right now. Please try again.");
      } finally {
        setAiLoading(false);
      }
    },
    [companyId, isDriver, provider],
  );

  const suggestions = useMemo(
    () => provider.suggestions({ companyId: companyId ?? "", isDriver }),
    [provider, companyId, isDriver],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0">
        <DialogTitle className="sr-only">Command bar</DialogTitle>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border bg-muted/40 px-3 py-2">
          {(["search", "ai"] as Tab[]).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                tab === value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {value === "search" ? <SearchIcon className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
              {value === "search" ? "Search" : "Ask"}
            </button>
          ))}
          <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">
            <kbd className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px]">esc</kbd> to close
          </span>
        </div>

        {tab === "search" ? (
          <Command shouldFilter={false} className="bg-background">
            <div className="flex items-center border-b border-border px-3">
              <SearchIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search jobs, customers, drivers, vehicles, invoices…"
                className="flex h-12 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                aria-label="Search"
              />
              {searching && <Loader2 className="ml-2 h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            <CommandList className="max-h-[60vh]">
              {!searching && groups.length === 0 && (
                <CommandEmpty>No results found.</CommandEmpty>
              )}
              {groups.map((group) => (
                <CommandGroup key={group.id} heading={group.label}>
                  {group.results.map((result) => (
                    <CommandItem
                      key={result.id}
                      value={result.id}
                      onSelect={() => go(result.to)}
                      className="flex items-center gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{result.title}</p>
                        {result.subtitle && (
                          <p className="truncate text-xs text-muted-foreground">{result.subtitle}</p>
                        )}
                      </div>
                      {result.badge && (
                        <Badge variant="secondary" className="shrink-0 text-[10px] capitalize">
                          {String(result.badge).replace(/_/g, " ")}
                        </Badge>
                      )}
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        ) : (
          <div className="flex flex-col">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void ask(aiQuestion);
              }}
              className="flex items-center border-b border-border px-3"
            >
              <Sparkles className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                autoFocus
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                placeholder="Ask an operational question…"
                className="flex h-12 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                aria-label="Ask a question"
              />
              {aiLoading && <Loader2 className="ml-2 h-4 w-4 animate-spin text-muted-foreground" />}
            </form>

            <div className="max-h-[60vh] overflow-y-auto p-4">
              {!aiAnswer && !aiLoading && (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Try asking
                  </p>
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => void ask(suggestion)}
                      className="block w-full rounded-md border border-border bg-card px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}

              {aiError && <p className="text-sm text-destructive">{aiError}</p>}

              {aiAnswer && !aiLoading && (
                <div className="space-y-3">
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{aiAnswer.headline}</h3>
                    <p className="text-sm text-muted-foreground">{aiAnswer.detail}</p>
                  </div>
                  {aiAnswer.bullets.length > 0 && (
                    <ul className="space-y-1.5">
                      {aiAnswer.bullets.map((bullet, index) => (
                        <li
                          key={index}
                          className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground"
                        >
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  )}
                  {aiAnswer.sources.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {aiAnswer.sources.map((source) =>
                        source.to ? (
                          <button
                            key={source.label}
                            type="button"
                            onClick={() => go(source.to as string)}
                            className="rounded-full border border-border px-3 py-1 text-xs text-foreground transition-colors hover:bg-accent"
                          >
                            {source.label}
                          </button>
                        ) : (
                          <span key={source.label} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">
                            {source.label}
                          </span>
                        ),
                      )}
                    </div>
                  )}
                  <p className="pt-1 text-[11px] text-muted-foreground">
                    Answered by {provider.label} ({aiAnswer.provider}) · confidence {aiAnswer.confidence}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
